import styled from "styled-components"

import { maxWidth, eq, when, minWidth } from "utils"

import CloudinaryImage from "components/cloudinary-image"
import CroppedImage from "components/cropped-image"

export const Container = styled.div`
  margin: ${({ isGrid }) => (isGrid ? "2rem 0rem" : "4rem 10rem")};
  border-radius: 0.6rem;
  display: ${({ hideOnPageLoad }) => (hideOnPageLoad ? "none" : "flex")};
  flex-direction: ${({ isGrid }) => (isGrid ? "column" : "row")};
  transition: all 0.25s ease-in-out;
  cursor: pointer;
  ${({ renderImage }) => (!renderImage ? "flex: 0 0 calc(50% - 1rem)" : "")};

  ${maxWidth.tablet`
    margin: ${({ isGrid }) => (isGrid ? "2rem 0rem" : "4rem 2rem")};
    flex-direction: column;
  `}

  ${maxWidth.mobile`
    margin: ${({ isGrid }) => (isGrid ? "2rem 0rem" : "4rem 2rem")};
    
    ${({ renderImage }) =>
      !renderImage &&
      `
      border-top: 0.075rem solid rgba(0, 0, 0, 0.25);
      padding-top: 2rem;
      border-radius: 0;
    `}
  `}

  ${eq("imagePosition", "bottom")`
    flex-direction: column-reverse !important;
  `}

  ${eq("imagePosition", "left")`
    flex-direction: row;
  `}

  ${eq("imagePosition", "right")`
    flex-direction: row-reverse;
  `}
`

export const Header = styled.div`
  width: ${({ isGrid }) => (isGrid ? "100%" : "50%")};
  padding: ${({ isGrid, hasImage }) => (isGrid && hasImage ? "2rem 0" : "0")};
  display: flex;
  flex-direction: column;
  flex: 1 0 auto;

  ${maxWidth.tablet`
    width: 100%;
  `}

  ${maxWidth.mobile`
    width: 100%;
  `}

  ${eq("imagePosition", "bottom")`
    padding: 0 0 1.5rem;
  `}

  ${eq("imagePosition", "left")`
    padding: 0 0 0 1.25rem;
    flex: 0 1 auto;
  `}

  ${eq("imagePosition", "right")`
    padding: 0 1.25rem 0 0;
    flex: 0 1 auto;
  `}
`

export const AboveTitle = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.4rem;
`

export const TopRight = styled.div`
  display: flex;
  align-items: center;
`

export const Headline = styled.h3`
  font-size: 2.4rem;
  font-weight: 400;
  line-height: 3rem;
  color: ${({ theme, headlineColor }) =>
    theme.component?.text || headlineColor || theme.colors.text.primary};

  ${eq("variant", "xlarge")`
    font-size: 3rem;
    line-height: 3.4rem;
  `}

  ${eq("variant", "medium")`
    font-size: 1.9rem;
    line-height: 2.6rem;
  `}

  ${eq("variant", "small")`
    font-size: 1.6rem;
    line-height: 2.0rem;
  `}

  ${maxWidth.mobile`
    ${({ mobileHeadlineSize }) =>
      mobileHeadlineSize &&
      `
      font-size: ${mobileHeadlineSize};
      line-height: 1.6rem;
    `}
  `}
`

export const Subheadline = styled.div`
  font-size: 1.6rem;
  line-height: 2.1rem;
  flex: 1 0 auto;
  margin: 0.75rem 0;
  color: ${({ theme }) => theme.component?.text || theme.colors.text.primary};

  :empty {
    margin: 0.75rem 0;
  }
`

export const MediaContainer = styled.div`
  position: relative;
  width: 100% !important;
  overflow: hidden;

  ${eq("imagePosition", "left")`
    width: calc(100% - 1.5rem) !important;
  `}

  ${eq("imagePosition", "right")`
    width: calc(100% - 1.5rem) !important;
  `}

  section > div {
    ${eq("variant", "2col")`
      padding-bottom: calc(582 / 865 * 100%);
    `}

    ${eq("variant", "3col")`
      padding-bottom: calc(246 / 430 * 100%);
    `}

    ${eq("variant", "4col")`
      padding-bottom: calc(193 / 287 * 100%);
    `}

    ${maxWidth.tablet`
      ${eq("variant", "4col")`
        padding-bottom: calc(234 / 443 * 100%);
      `}
    `}

    ${maxWidth.mobile`
      ${eq("variant", "3col")`
        padding-bottom: calc(171 / 383 * 100%);
      `}

      ${eq("variant", "4col")`
        padding-bottom: calc(193 / 287 * 100%);
      `}
    `}

    ${when("squashImageVertically")`
      ${eq("variant", "2col")`
        padding-bottom: calc(418 / 662 * 100%);
      `}

      ${eq("variant", "4col")`
        padding-bottom: calc(146 / 321 * 100%);
      `}

      ${maxWidth.mobile`
        ${eq("variant", "2col")`
          padding-bottom: calc(268 / 350 * 100%);
        `}
      `}
    `}

    ${eq("imagePosition", "left")`
      padding-bottom: calc(160 / 187 * 100%);
    `}

    ${eq("imagePosition", "right")`
      padding-bottom: calc(160 / 187 * 100%);
    `}
  }
`

export const MediaIcon = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`

export const BottomSign = styled.div`
  position: absolute;
  bottom: 0;
  right: 0;
`

export const Byline = styled.p`
  margin: 0;
  font-size: 1.2rem;
  line-height: 1.3rem;
  color: ${({ theme, bylineColor }) =>
    theme.component?.textSecondary || bylineColor || theme.colors.text.primary};

  ${minWidth.mobile`
    font-size: 1.4rem;
  `}
`

export const BylineAuthor = styled(Byline)`
  text-transform: capitalize;
`

export const BylineReviewed = styled(Byline)`
  margin-top: 0.15rem;
`

export const ImageRegular = styled(CloudinaryImage)`
  ${eq("variant", "2col")`
  padding-bottom: calc(582 / 865 * 100%);
`}

  ${eq("variant", "3col")`
  padding-bottom: calc(246 / 430 * 100%);
`}

${eq("variant", "4col")`
  padding-bottom: calc(193 / 287 * 100%);
`}

${maxWidth.tablet`
  ${eq("variant", "4col")`
    padding-bottom: calc(234 / 443 * 100%);
  `}
`}

${maxWidth.mobile`
  ${eq("variant", "3col")`
    padding-bottom: calc(171 / 383 * 100%);
  `}

  ${eq("variant", "4col")`
    padding-bottom: calc(193 / 287 * 100%);
  `}
`}

${eq("imagePosition", "left")`
  padding-bottom: calc(160 / 187 * 100%);
`}

${eq("imagePosition", "right")`
  padding-bottom: calc(160 / 187 * 100%);
`}

${when("squashImageVertically")`
  padding-bottom: calc(100% / 1.8) !important;
`}

${when("zoomImageOnHover")`
  overflow: hidden;
  transition: transform 0.2s linear;

  &:hover {
    transform: scale(1.25);
  }
`}
`

export const ImageCropped = styled(CroppedImage)`
  ${eq("variant", "2col")`
  padding-bottom: calc(582 / 865 * 100%);
`}

  ${eq("variant", "3col")`
  padding-bottom: calc(246 / 430 * 100%);
`}

${eq("variant", "4col")`
  padding-bottom: calc(193 / 287 * 100%);
`}

${maxWidth.tablet`
  ${eq("variant", "4col")`
    padding-bottom: calc(234 / 443 * 100%);
  `}
`}

${maxWidth.mobile`
  ${eq("variant", "3col")`
    padding-bottom: calc(171 / 383 * 100%);
  `}

  ${eq("variant", "4col")`
    padding-bottom: calc(193 / 287 * 100%);
  `}
`}

${eq("imagePosition", "left")`
  padding-bottom: calc(160 / 187 * 100%);
`}

${eq("imagePosition", "right")`
  padding-bottom: calc(160 / 187 * 100%);
`}

${when("squashImageVertically")`
  padding-bottom: calc(100% / 1.8) !important;
`}

${when("zoomImageOnHover")`
  overflow: hidden;
  transition: transform 0.2s linear;

  &:hover {
    transform: scale(1.25);
  }
`}
`
